import {useNuxtApp} from 'nuxt/app'
import {useUserStore} from '@/store/user'
import {useAuthStore} from '@/store/auth'
import {useTargetedMessagesStore} from '@/store/targeted-messages'
import {useKeycloakService} from '~/composables/keycloak_service'
import * as Sentry from '@sentry/browser'
import type {KeycloakConfig} from './types'
import {restoreUserStoreFromAccessToken, buildKeycloakUser} from './useKeycloakToken'
import {clearKeycloakState, resolveRedirectPath} from './useKeycloakSession'
import {useKeycloakUser} from './useKeycloakUser'

async function loginWithKeycloak(returnPath?: string) {
  if (!process.client) {return}

  try {
    const app = useNuxtApp()
    const userManager = app.$userManager

    const state = {
      signInRedirect: returnPath ?? '/eu/en',
      locale: app.$i18n?.locale?.value ?? 'en',
      region: app.$globalization?.getRegion() ?? 'eu'
    }

    await userManager.signinRedirect({
      state,
      extraQueryParams: {
        lang: state.locale,
        region: state.region
      }
    })
  } catch (error) {
    Sentry.captureException(error, {tags: {source: 'keycloak_login'}})
  }
}

async function handleKeycloakCallback(): Promise<string | null> {
  if (!process.client) {
    return null
  }

  const {fetchKeycloakUser} = useKeycloakUser()

  try {
    const app = useNuxtApp()
    const userManager = app.$userManager

    // Parse URL to extract authorization code and state
    const urlParams = new URLSearchParams(globalThis.location.search)
    const code = urlParams.get('code')
    const state = urlParams.get('state')

    if (!code || !state) {
      return null
    }

    // Retrieve stored OIDC state (includes code_verifier for PKCE)
    const stateKey = `ENuxtKeycloakState.${state}`
    const storedStateJson = localStorage.getItem(stateKey)

    if (!storedStateJson) {
      Sentry.captureMessage('Keycloak state not found in storage', 'warning')
      return null
    }

    const storedState = JSON.parse(storedStateJson)
    const codeVerifier = storedState.code_verifier
    const redirectUri = storedState.redirect_uri ?? useRuntimeConfig().public.BASE_URL + '/auth'

    if (!codeVerifier) {
      Sentry.captureMessage('Code verifier not found in stored state', 'warning')
      return null
    }

    localStorage.removeItem(stateKey)

    const keycloakService = useKeycloakService()
    const authStore = useAuthStore()
    const userStore = useUserStore()

    if (userStore.customerUuidLink && userStore.customerUuidLink !== 'default') {
      // @ts-ignore - userStore.newCustomer can accept string
      userStore.newCustomer = userStore.customerUuidLink
    }
    userStore.customerUuidLink = ''

    const customerUuid = userStore.newCustomer ?? userStore.selectedCustomer?.customerUuid ?? 'default'

    const response = await keycloakService.exchangeKeycloakCode(customerUuid, code, codeVerifier, redirectUri)

    if (!response?.data?.accessToken) {
      Sentry.captureMessage('No access token received from backend', 'warning')
      return null
    }

    restoreUserStoreFromAccessToken(response.data.accessToken)
    const user = buildKeycloakUser(
      response.data.accessToken,
      response.data.idToken,
      response.data.refreshToken,
      storedState.data
    )

    await userManager.storeUser(user)
    authStore.setUserObject(user)

    const targetedMessagesStore = useTargetedMessagesStore()
    targetedMessagesStore.resetTemporaryDismissals()

    await fetchKeycloakUser(userManager)

    return resolveRedirectPath(storedState.data?.signInRedirect)
  } catch (error) {
    Sentry.captureException(error, {tags: {source: 'keycloak_callback'}})
    return null
  }
}

async function handleKeycloakRestoration(to: any) {
  if (!process.client) {return}

  const {fetchKeycloakUser} = useKeycloakUser()
  const authStore = useAuthStore()
  const userStore = useUserStore()

  const needsRestore = !authStore.isTokenExpired
    && authStore?.userObject
    && userStore?.userUuid === ''
    && !to.fullPath.startsWith('/auth#state')
    && !to.fullPath.startsWith('/auth?')

  if (needsRestore) {
    const app = useNuxtApp()
    const userManager = app.$userManager

    try {
      const storedUser = await userManager.getUser()

      if (!storedUser || !storedUser.access_token) {
        Sentry.captureMessage('No stored user found during Keycloak restoration', 'warning')
        await clearKeycloakState(userManager)
        return
      }

      restoreUserStoreFromAccessToken(storedUser.access_token)

      await fetchKeycloakUser(userManager)
    } catch (error) {
      Sentry.captureException(error, {tags: {source: 'keycloak_restoration'}})
      await clearKeycloakState(userManager)
    }
  }

  if (authStore.userObject && authStore.isTokenExpired) {
    const app = useNuxtApp()
    const userManager = app.$userManager
    await clearKeycloakState(userManager)
  }
}

async function logoutFromKeycloak() {
  if (!process.client) {return}

  try {
    const app = useNuxtApp()
    const userManager = app.$userManager

    const user = await userManager.getUser()
    const config = useRuntimeConfig().public
    const keycloakConfig = config.keycloak as KeycloakConfig
    const baseUrl = config.BASE_URL

    const postLogoutRedirectUri = baseUrl + '/'

    await clearKeycloakState(userManager)

    const logoutUrl = `${keycloakConfig.authority}/protocol/openid-connect/logout`
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = logoutUrl

    const redirectInput = document.createElement('input')
    redirectInput.type = 'hidden'
    redirectInput.name = 'post_logout_redirect_uri'
    redirectInput.value = postLogoutRedirectUri
    form.appendChild(redirectInput)

    if (user?.id_token) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'id_token_hint'
      tokenInput.value = user.id_token
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  } catch (error) {
    Sentry.captureException(error, {tags: {source: 'keycloak_logout'}})
    const app = useNuxtApp()
    globalThis.location.href = app.localePath('/', null, null)
  }
}

async function handleKeycloakBeforeMount(): Promise<void> {
  const app = useNuxtApp()
  const authStore = useAuthStore()
  const {CookieStorage} = await import('cookie-storage')
  const cookieStorage = new CookieStorage({
    expires: new Date(Date.now() + 3600 * 1000),
    path: '/'
  })

  const existingUser = await app.$userManager.getUser()

  if (existingUser && existingUser.expired) {
    await app.$userManager.removeUser()
    cookieStorage.removeItem('ENuxtKeycloakUser.sub')
  } else if (existingUser) {
    if (app.payload?.serverRendered) {
      if (existingUser.profile?.sub) {
        cookieStorage.setItem('ENuxtKeycloakUser.sub', existingUser.profile.sub)
      }
      // eslint-disable-next-line no-console
      console.log('ssr mismatch, reloading')
      const {reloadNuxtApp} = await import('nuxt/app')
      reloadNuxtApp()
    } else {
      if (authStore.userFetchInProgress) {
        return
      }
      await handleKeycloakRestoration({fullPath: globalThis.location.pathname + globalThis.location.hash})
    }
  }
}

export const useKeycloakAuth = () => {
  return {
    loginWithKeycloak,
    handleKeycloakCallback,
    handleKeycloakRestoration,
    logoutFromKeycloak,
    handleKeycloakBeforeMount
  }
}
